/**
 * useTvdbBatch -- Batch-resolve TVDB canonical artwork for a collection of items.
 *
 * Architecture mirrors useTmdbBatch exactly:
 * - sessionStorage-backed persistent cache (survives page refresh within session)
 * - Module-level Map mirrors storage for instant access (no JSON parse per read)
 * - Incremental: only fetches items not already in cache
 * - Fire-and-forget fetches: no AbortController, runs to completion
 * - Chunks requests to stay within the 60-item batch endpoint limit
 * - Deduplicates in-flight requests to prevent double-fetching
 *
 * Items without a tvdbId are absent from the map — callers fall back to
 * Emby/Jellyfin thumbnail proxy URLs.
 */

import { useEffect, useMemo, useRef, useState } from "react";

import { useCoverArtContext } from "../contexts/CoverArtSettingsContext";
import type { TvdbImageData } from "./useTvdbImages";
import { buildTvdbProxyUrl } from "./useTvdbImages";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface TvdbBatchItem {
  id: string;
  tvdbId: string | null | undefined;
  type: string;
}

type TvdbBatchPayloadItem = { tvdbId: string; mediaType: "movie" | "tv"; itemId: string };

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const CHUNK_SIZE = 60;
const STORAGE_KEY = "wsd-tvdb-cache";

// ─────────────────────────────────────────────────────────────────────────────
// Module-level persistent cache + sessionStorage backing
// ─────────────────────────────────────────────────────────────────────────────

const tvdbCache = new Map<string, TvdbImageData>();
const inFlightIds = new Set<string>();
const listeners = new Set<() => void>();

let flushTimer: ReturnType<typeof setTimeout> | null = null;

function persistToStorage(): void {
  try {
    const obj: Record<string, TvdbImageData> = {};
    for (const [k, v] of tvdbCache) {
      obj[k] = v;
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch { /* storage full or unavailable */ }
}

function schedulePersist(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    persistToStorage();
  }, 500);
}

function notifyListeners(): void {
  for (const cb of listeners) cb();
}

function hydrateFromStorage(): void {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const obj = JSON.parse(raw) as Record<string, TvdbImageData>;
    for (const [k, v] of Object.entries(obj)) {
      if (v) {
        tvdbCache.set(k, v);
      }
    }
  } catch { /* corrupt or unavailable */ }
}

hydrateFromStorage();

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function toTvdbMediaType(type: string): "movie" | "tv" | null {
  if (type === "Movie") return "movie";
  if (type === "Series" || type === "Episode") return "tv";
  return null;
}

export function getTvdbPosterUrl(
  map: Map<string, TvdbImageData>,
  itemId: string,
): string | null {
  const entry = map.get(itemId);
  if (!entry?.bestPoster) return null;
  return buildTvdbProxyUrl(entry.bestPoster.filePath, "poster");
}

export function getTvdbBackdropUrl(
  map: Map<string, TvdbImageData>,
  itemId: string,
): string | null {
  const entry = map.get(itemId);
  if (!entry?.bestBackdrop) return null;
  return buildTvdbProxyUrl(entry.bestBackdrop.filePath, "backdrop");
}

async function fetchAndCacheChunk(
  chunk: TvdbBatchPayloadItem[],
): Promise<void> {
  try {
    const res = await fetch("/api/streaming/tvdb/images/batch", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "include",
      body: JSON.stringify({ items: chunk }),
    });
    const json = (await res.json()) as { ok?: boolean; data?: { results: Record<string, TvdbImageData> } };
    if (json.ok && json.data?.results) {
      for (const [itemId, data] of Object.entries(json.data.results)) {
        // Cache ALL returned entries — including negatives (both null).
        // This prevents infinite retry loops for items that have no TVDB art.
        tvdbCache.set(itemId, data);
      }
      schedulePersist();
    }
  } catch {
    // Network error — items will be retried on next render cycle
  } finally {
    for (const item of chunk) {
      inFlightIds.delete(item.itemId);
    }
  }
}

function fetchNeeded(items: TvdbBatchItem[]): void {
  const needed: TvdbBatchPayloadItem[] = [];
  for (const item of items) {
    if (!item.tvdbId) continue;
    if (tvdbCache.has(item.id)) continue;
    if (inFlightIds.has(item.id)) continue;
    const mediaType = toTvdbMediaType(item.type);
    if (!mediaType) continue;
    needed.push({ tvdbId: item.tvdbId, mediaType, itemId: item.id });
  }
  if (needed.length === 0) return;

  for (const item of needed) {
    inFlightIds.add(item.itemId);
  }

  const chunks: TvdbBatchPayloadItem[][] = [];
  for (let i = 0; i < needed.length; i += CHUNK_SIZE) {
    chunks.push(needed.slice(i, i + CHUNK_SIZE));
  }

  void Promise.allSettled(chunks.map((chunk) => fetchAndCacheChunk(chunk)))
    .then(() => notifyListeners());
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useTvdbBatch(
  items: TvdbBatchItem[],
): Map<string, TvdbImageData> {
  const { tvdbEnabled } = useCoverArtContext();

  const [tick, setTick] = useState(0);

  const itemsRef = useRef(items);
  itemsRef.current = items;

  useEffect(() => {
    const cb = () => setTick((t) => t + 1);
    listeners.add(cb);
    return () => { listeners.delete(cb); };
  }, []);

  useEffect(() => {
    if (tvdbEnabled) fetchNeeded(items);
  }, [items, tick, tvdbEnabled]);

  return useMemo(() => {
    if (!tvdbEnabled) return new Map<string, TvdbImageData>();
    const map = new Map<string, TvdbImageData>();
    for (const item of items) {
      const cached = tvdbCache.get(item.id);
      if (cached) map.set(item.id, cached);
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, tick, tvdbEnabled]);
}
